/**
 * importExportService.ts
 *
 * Uses the shared apiService instance so the base URL is always in sync with
 * the runtime config.json (avoids the module-load-time race condition) and
 * authentication is handled by the existing request interceptor.
 */
import apiService from './ApiService';

// ── Types ─────────────────────────────────────────────────────────────────────

export type ExportType = 'full' | 'update' | 'create';
export type ImportType = 'create' | 'update';

export interface ExportFilters {
  domain_id?: string;
  /** Generic column→value filter map (LIKE), matching DynamicService.search. */
  filters?: Record<string, string>;
  /** Only include these columns in the exported CSV. Omit to export all columns. */
  fields?: string[];
}

export interface ExportJobResponse {
  file_id: string;
  export_type: ExportType;
  filename: string;
  message: string;
}

export interface ImportErrorDetail {
  location: string;
  message: string;
}

export interface ImportResponse {
  success: boolean;
  import_type: ImportType;
  domain_id?: string;
  rows_processed: number;
  rows_accepted: number;
  rows_rejected: number;
  errors: ImportErrorDetail[];
  message: string;
}

// ── Export ────────────────────────────────────────────────────────────────────

/**
 * Step 1 — request export file generation.
 * Maps the ExportDialog option to the corresponding endpoint:
 *   'full'   -> POST /api/export/full
 *   'update' -> POST /api/export/update
 *   'create' -> POST /api/export/create
 */
export async function requestExport(
  type: ExportType,
  filters: ExportFilters = {},
): Promise<ExportJobResponse> {
  const res = await apiService.getApiClient().post<ExportJobResponse>(
    `/api/export/${type}`,
    filters,
  );
  return res.data;
}

/**
 * Step 2 — download the generated file and trigger a browser download.
 */
export async function downloadExportFile(fileId: string, filename: string): Promise<void> {
  const res = await apiService.getApiClient().get(`/api/export/download/${fileId}`, {
    responseType: 'blob',
  });
  const url = URL.createObjectURL(new Blob([res.data]));
  try {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
  } finally {
    // Delay revoke so the browser has time to start the download
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
  }
}

/**
 * Convenience — request and download in a single call.
 */
export async function exportAndDownload(
  type: ExportType,
  filters: ExportFilters = {},
): Promise<void> {
  const job = await requestExport(type, filters);
  await downloadExportFile(job.file_id, job.filename);
}

// ── Import ────────────────────────────────────────────────────────────────────

/**
 * Upload a CSV file to the backend (multipart/form-data).
 */
export async function importFile(type: ImportType, file: File, domainId?: string): Promise<ImportResponse> {
  const formData = new FormData();
  formData.append('import_type', type);
  if (domainId) formData.append('domain_id', domainId);
  formData.append('file', file);

  // Do NOT set Content-Type manually — Axios detects FormData and sets the correct
  // multipart/form-data boundary automatically. A manual header would omit the boundary.
  const res = await apiService.getApiClient().post<ImportResponse>('/api/import', formData);
  return res.data;
}
